import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch } from '@/redux/hooks';
import { fetchSavedSearchItemsIfNeeded } from '@/redux/modules/savedSearch';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { addImageParams, getCategoryPageUrl } from '@/utils/urls';

import FollowSearchButton from './FollowSearchButton';

type Props = {
    categoryId: string;
    categoryUrl: string;
    followerCount: number;
    images: string[];
    itemCount: number;
    name: string;
    saveSearchStateKey: string;
};

export const SearchFollowSuggestionsCard = ({
    categoryId,
    categoryUrl,
    followerCount,
    images,
    itemCount,
    name,
    saveSearchStateKey,
}: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const trackClick = useCallback(() => {
        dispatch(fetchSavedSearchItemsIfNeeded(true));
    }, [dispatch]);

    return (
        <div className="h-[215px] w-[285px] min-w-[285px] border border-border-secondary [&_a]:no-underline">
            <RouteLink to={`${getCategoryPageUrl(categoryUrl, categoryId)}`}>
                <div className="bg-white flex border-b border-border-secondary">
                    {images.map((imageUrl, index) => (
                        <div
                            className="relative mr-[1px] flex h-[72px] w-[94px] items-center justify-center overflow-hidden bg-[#e0dad3] last:mr-0"
                            key={`thumbnail-${index}`}
                        >
                            <img
                                alt={`${name} - ${index + 1} - image`}
                                className="max-h-[calc(100%+16px)] max-w-[calc(100%+16px)]"
                                loading="lazy"
                                src={addImageParams({
                                    url: imageUrl,
                                    width: 188,
                                })}
                            />
                        </div>
                    ))}
                </div>
                <div className="mx-[13px] mt-[15px]">
                    <div className="font-secondary text-[15px] leading-[21px] tracking-[0.5px] text-text-primary">
                        {name}
                    </div>
                    <BodySecondary
                        className="mt-[6px] tracking-[0.5px]"
                        color="secondary"
                    >
                        <FormattedNumber
                            key="search-follow-suggestions-card-item-count"
                            style="decimal"
                            value={itemCount}
                        />{' '}
                        {formatMessage(['searchFollowSuggestions', 'items'])}{' '}
                        <FormattedNumber
                            key="search-follow-suggestions-card-follower-count"
                            style="decimal"
                            value={followerCount}
                        />{' '}
                        {formatMessage(['searchFollowSuggestions', 'followers'])}
                    </BodySecondary>
                </div>
            </RouteLink>
            <div className="ml-[13px] mt-[15px] [&_button[class*='button-saved']]:cursor-auto [&_button]:w-[258px]">
                <FollowSearchButton
                    categoryId={categoryId}
                    onClick={trackClick}
                    saveSearchKey={saveSearchStateKey}
                />
            </div>
        </div>
    );
};
